import { TriangleAlertIcon, WalletIcon } from 'lucide-react';

import React from 'react';

import { useTranslation } from '@splitfiesta/i18n';
import { Card } from '@splitfiesta/ui';

import CurrencyInput from '@/components/ui/CurrencyInput';
import SearchableCurrencySelect from '@/components/ui/SearchableCurrencySelect';

import { getInputClasses } from '@/lib/styles/form';

interface GroupMoneySettingsProps {
  currency: string;
  setCurrency: (currency: string) => void;
  budget: string | undefined;
  setBudget: (budget: string | undefined) => void;
  numericBudget: number;
  setNumericBudget: (budget: number) => void;
  isLoading: boolean;
  isDeleting: boolean;
  mode?: 'create' | 'edit';
  /**
   * Lock the currency select (read-only). Currency is immutable after a
   * group is created — the backend strips it from updates to protect
   * per-currency balances — so edit mode must not offer a picker that
   * silently does nothing.
   */
  currencyLocked?: boolean;
  /** Per-field edit permission flags (undefined = permitted). */
  canEditCurrency?: boolean;
  canEditBudget?: boolean;
  /** Subtle hint shown beneath a locked field. */
  lockedHint?: string;
}

export function GroupMoneySettings({
  currency,
  setCurrency,
  budget,
  setBudget,
  numericBudget: _numericBudget,
  setNumericBudget,
  isLoading,
  isDeleting,
  mode = 'create',
  currencyLocked = false,
  canEditCurrency = true,
  canEditBudget = true,
  lockedHint,
}: GroupMoneySettingsProps) {
  const { t } = useTranslation();

  return (
    <Card>
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center">
          <WalletIcon className="w-5 h-5 mr-2" />
          {t('groups:sections.expenseTracking')}
        </h3>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
          <div>
            <label
              htmlFor="currency"
              className="block text-sm font-medium text-gray-700 dark:text-white mb-1"
            >
              {t('groups:fields.defaultCurrency')}
            </label>
            <SearchableCurrencySelect
              id="currency"
              value={currency}
              onChange={setCurrency}
              mode="group"
              disabled={
                isLoading || isDeleting || currencyLocked || !canEditCurrency
              }
              data-testid="create-group-currency-select"
            />
            {mode === 'create' && !currencyLocked && canEditCurrency ? (
              // The choice is only free until the first expense, and this is
              // the one screen where it is still free.
              <p
                data-testid="currency-lock-warning"
                className="mt-2 flex items-start gap-2 rounded-lg border border-warning-200 bg-warning-50 p-3 text-sm font-medium text-warning-800 dark:border-warning-800 dark:bg-warning-900/20 dark:text-warning-300"
              >
                <TriangleAlertIcon
                  className="mt-0.5 h-4 w-4 shrink-0"
                  aria-hidden="true"
                />
                <span>{t('groups:fields.currencyLockHintConversion')}</span>
              </p>
            ) : (
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                {currencyLocked
                  ? t('groups:fields.currencyLockedHelp')
                  : !canEditCurrency && lockedHint
                    ? lockedHint
                    : mode === 'create'
                      ? t('groups:fields.currencyLockHintConversion')
                      : t('groups:fields.defaultCurrencyHelp')}
              </p>
            )}
          </div>

          <div>
            <label
              htmlFor="budget"
              className="block text-sm font-medium text-gray-700 dark:text-white mb-1"
            >
              {t('groups:fields.budget')}
            </label>
            <CurrencyInput
              id="budget"
              name="budget"
              value={budget}
              currency={currency || 'USD'}
              placeholder={t('groups:fields.budgetPlaceholder')}
              onValueChange={(value, _name, values) => {
                setBudget(value);
                setNumericBudget(values?.float || 0);
              }}
              className={getInputClasses({
                disabled: isLoading || isDeleting || !canEditBudget,
              })}
              disabled={isLoading || isDeleting || !canEditBudget}
            />
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              {!canEditBudget && lockedHint
                ? lockedHint
                : t('groups:fields.budgetHelp')}
              {canEditBudget &&
                budget &&
                currency &&
                ` ${t('groups:fields.budgetCurrencyNote', { currency })}`}
            </p>
          </div>
        </div>
      </div>
    </Card>
  );
}

export default GroupMoneySettings;
